import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty, min } from "lodash";
import axios from "axios";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import { actionAppDateUpdated } from "../App/AppRedux";
import ListItemButton from "@mui/material/ListItemButton";

const LAST_UPDATE_URL = `${import.meta.env.VITE_API_BASE_URL}/lastupdated`;

const common_ = (theme) => ({
  // 44x44 min size is needed to meet this accessibility requirement:
  // https://www.w3.org/WAI/WCAG22/Understanding/target-size-enhanced.html
  minHeight: 44,
  paddingTop: 2,
  fontSize: ".75rem",
  // Override to meet "Line height is below minimum value" requirements
  // in Siteimprove.
  lineHeight: "1.5",
});

const date_ = (theme) => ({
  fontStyle: "italic",
});

const type_ = (theme) => ({});

const container_ = (theme) => ({
  width: "20vw",
  minWidth: "260px",
});

const rowGrid_ = (theme) => ({
  direction: "row",
  justifyContent: "space-between",
});

const popover_ = (theme) => ({
  width: "calc(20vw - 32px)",
  minWidth: "calc(260px - 32px)",
  padding: "10px",
});

export default function MenuStatus() {
  const dispatch = useDispatch();
  const status = useSelector((state) => state.appReducer.lastUpdated);
  const [anchorEl, setAnchorEl] = useState(null);

  const dataFetched = !isEmpty(status) && formatDate(status.data_fetched_at);
  const tileGenerated =
    !isEmpty(status) && formatDate(status.tile_generated_at);
  const dateStr = getEarliestDate(status);

  const handleClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  useEffect(() => {
    if (isEmpty(status)) {
      setStatus(dispatch, actionAppDateUpdated);
    }
  }, [status]);

  return (
    <Box sx={container_}>
      {isEmpty(status) ? (
        ""
      ) : (
        <>
          <ListItemButton onClick={handleClick}>
            <Grid container>
              <Grid container sx={rowGrid_}>
                <Grid>
                  <Typography variant="body2" sx={[type_, common_]}>
                    Last Updated:
                  </Typography>
                </Grid>
                <Grid>
                  <Typography variant="body2" sx={[date_, common_]}>
                    {dateStr}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </ListItemButton>
          <Popover
            id={id}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            slotProps={{
              paper: {
                sx: popover_,
              },
            }}
          >
            <Grid container>
              <Grid container sx={rowGrid_}>
                <Grid>
                  <Typography variant="body2" sx={type_}>
                    Data Fetched:
                  </Typography>
                </Grid>
                <Grid>
                  <Typography variant="body2" sx={date_}>
                    {dataFetched}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container sx={rowGrid_}>
                <Grid>
                  <Typography variant="body2" sx={type_}>
                    Tiles Generated:
                  </Typography>
                </Grid>
                <Grid>
                  <Typography variant="body2" sx={date_}>
                    {tileGenerated}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Popover>
        </>
      )}
    </Box>
  );
}

function setStatus(dispatch, actionAppDateUpdated) {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const tileAndDataPromise = axios.get(LAST_UPDATE_URL, {
    cancelToken: source.token,
  });

  Promise.all([tileAndDataPromise])
    .then((responses) => {
      const [tileAndDataRes] = responses;
      if (
        tileAndDataRes &&
        tileAndDataRes.data &&
        tileAndDataRes.data.data_fresh &&
        tileAndDataRes.data.data_fetched_at &&
        tileAndDataRes &&
        tileAndDataRes.data &&
        tileAndDataRes.data.tile_fresh &&
        tileAndDataRes.data.tile_generated_at
      ) {
        dispatch(
          actionAppDateUpdated({
            data_fetched_at: tileAndDataRes.data.data_fetched_at,
            tile_generated_at: tileAndDataRes.data.tile_generated_at,
          }),
        );
      }
    })
    .catch(function () {
      return;
    });
}

function formatDate(utcString) {
  const date = new Date(utcString);
  return `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
}

function getEarliestDate(status) {
  const earliestDate =
    !isEmpty(status) &&
    min([new Date(status.data_fetched_at), new Date(status.tile_generated_at)]);
  return (
    !isEmpty(status) &&
    `${earliestDate.getMonth() + 1}-${earliestDate.getDate()}-${earliestDate.getFullYear()}`
  );
}
